import { Check } from 'lucide-react';
import { cn } from '@/components/ui';

/**
 * StepWizard — 템플릿 등록/편집 단계 진행 표시기 (디자인 스펙 §4.5). 알림 feature 로컬.
 *
 * 상태 3종: 완료(accent 채움 + 체크) / 현재(accent 테두리 + red 숫자 + underline) / 예정(muted).
 * 완료된 단계는 클릭으로 되돌아가기 허용(onStepClick + 검증 통과 단계만 호출부가 제어).
 */
export interface WizardStep {
  key: string;
  label: string;
}

export interface StepWizardProps {
  steps: ReadonlyArray<WizardStep>;
  /** 현재 단계 인덱스(0-base). */
  current: number;
  /** 완료 단계 클릭 콜백(되돌아가기). 미지정 시 클릭 불가. */
  onStepClick?: (index: number) => void;
  className?: string;
}

export const StepWizard = ({ steps, current, onStepClick, className }: StepWizardProps) => (
  <ol
    className={cn('flex w-full items-center', className)}
    aria-label="진행 단계"
    data-testid="step-wizard"
  >
    {steps.map((step, i) => {
      const isComplete = i < current;
      const isCurrent = i === current;
      const clickable = isComplete && Boolean(onStepClick);
      return (
        <li key={step.key} className="flex flex-1 items-center last:flex-none">
          <button
            type="button"
            disabled={!clickable}
            onClick={clickable ? () => onStepClick?.(i) : undefined}
            aria-current={isCurrent ? 'step' : undefined}
            className={cn(
              'flex shrink-0 items-center gap-2',
              clickable ? 'cursor-pointer' : 'cursor-default',
              'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)] rounded-md',
            )}
          >
            <span
              aria-hidden="true"
              className="flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold"
              style={
                isComplete
                  ? { background: 'var(--accent-default)', color: '#fff' }
                  : isCurrent
                    ? {
                        background: 'var(--surface-0)',
                        border: '2px solid var(--accent-default)',
                        color: 'var(--accent-default)',
                      }
                    : {
                        background: 'var(--surface-0)',
                        border: '1px solid var(--divider-strong)',
                        color: 'var(--content-soft)',
                      }
              }
            >
              {isComplete ? <Check size={14} /> : i + 1}
            </span>
            <span
              className={cn(
                'hidden whitespace-nowrap text-[13px] sm:inline',
                isCurrent && 'border-b-2 pb-0.5',
              )}
              style={{
                color: isCurrent
                  ? 'var(--content-strong)'
                  : isComplete
                    ? 'var(--content-default)'
                    : 'var(--content-muted)',
                fontWeight: isCurrent ? 600 : 500,
                borderColor: isCurrent ? 'var(--accent-default)' : 'transparent',
              }}
            >
              {step.label}
            </span>
          </button>
          {i < steps.length - 1 ? (
            <span
              aria-hidden="true"
              className="mx-3 h-px flex-1"
              style={{
                background: isComplete ? 'var(--accent-default)' : 'var(--divider-strong)',
              }}
            />
          ) : null}
        </li>
      );
    })}
  </ol>
);
